import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type Column as TableColumn,
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnPinningState,
  type FilterFn,
  type SortingFn,
  type SortingState,
  type Table as TableInstance,
  type VisibilityState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Columns3,
  Download,
  EyeOff,
  Filter as FilterIcon,
  MoreVertical,
  PinOff,
  ArrowLeftToLine,
  ArrowRightToLine,
  Rows3,
  Rows4,
  Search,
  Maximize2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

/**
 * AG Grid-grade data table backed by `@tanstack/react-table` (engine)
 * + `@tanstack/react-virtual` (windowing). Public API stays
 * backward-compatible with the original component so existing call
 * sites don't need to change; new features are opt-in via optional
 * props.
 *
 * Layout (each section renders only when needed):
 *
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ Toolbar  [search]               [density][cols][csv]     │
 *   ├──────────────────────────────────────────────────────────┤
 *   │ Selection bar  "5 selected · {actions} · Clear"          │
 *   ├──────────────────────────────────────────────────────────┤
 *   │ Viewport (fixed height, internal scroll, virtualized)    │
 *   │   Sticky header                                          │
 *   │   Body (rows render via virtualizer)                     │
 *   ├──────────────────────────────────────────────────────────┤
 *   │ Footer pagination  "30 of 234 · [page size]   [< 1/8 >]"│
 *   └──────────────────────────────────────────────────────────┘
 *
 * Pencil components `fQcGz` / `VZeXz` / `dguJc` / `uLy1D` / `hJNwM`.
 */

export type ColumnFilterType<T = unknown> =
  | { type: "text"; placeholder?: string }
  | { type: "number"; placeholder?: string }
  | { type: "date" }
  | {
      type: "select";
      /** Explicit options; if omitted, derived from the column's distinct values. */
      options?: Array<{ value: string; label?: string }>;
    }
  | { type: "boolean"; trueLabel?: string; falseLabel?: string }
  | {
      /** Escape hatch — caller renders the input and owns the value. */
      type: "custom";
      render: (
        value: unknown,
        setValue: (v: unknown) => void,
        rows: T[],
      ) => ReactNode;
    };

export type Column<T> = {
  key: string;
  header: ReactNode;
  /** Default left-aligned. Pass "right" or "center" for numeric / icon columns. */
  align?: "left" | "right" | "center";
  /** Initial width — px (e.g. "200" or "200px") or fraction (e.g. "30%"). */
  width?: string;
  cell: (row: T) => ReactNode;
  /** Per-column override of table-level `sortable`. */
  sortable?: boolean;
  /** Per-column override of table-level `resizable`. */
  resizable?: boolean;
  /** Hideable via the column-visibility menu. Defaults to true. */
  hideable?: boolean;
  /** Header label for CSV export. Defaults to a stringified `header`. */
  csvHeader?: string;
  /** Cell value for CSV export. Defaults to a String() of `cell(row)`. */
  csvCell?: (row: T) => string;
  /** Plain-text accessor used for the global search input. Defaults to
   *  `csvCell` if defined, otherwise a best-effort stringification of
   *  the cell's React node. */
  searchAccessor?: (row: T) => string;
  /** Value used for sorting. Returns a number, Date, boolean, or
   *  string. When omitted, sort uses the same key as the global
   *  search (≈ stringified cell text), which is correct for text
   *  columns but wrong for currency / numeric / date strings. */
  sortAccessor?: (
    row: T,
  ) => string | number | boolean | Date | null | undefined;

  /** Per-column floating filter. When any column declares one, a filter
   *  row renders below the main header. */
  filter?: ColumnFilterType<T>;
  /** Plain accessor used for filtering / autosize / select-option derivation
   *  when `cell` returns React nodes. Defaults to `cell(row)` stringified. */
  filterAccessor?: (row: T) => string | number | boolean | Date | null | undefined;
  /** Hide the per-column kebab menu for this column. */
  noMenu?: boolean;
};

type ServerPagination = {
  pageIndex: number;
  pageSize: number;
  pageCount: number;
  totalRows?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
};

type SelectionConfig<T> = {
  rowIds: Set<string>;
  onChange: (ids: Set<string>) => void;
  /** Slot rendered above the table when one or more rows are selected. */
  actions?: (selectedRows: T[]) => ReactNode;
};

type DataTableProps<T> = {
  /* ====== Original API (unchanged) ====== */
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** When provided, prepends an expander column; renders panel below the row. */
  expandable?: (row: T) => ReactNode | null;
  emptyState?: ReactNode;
  className?: string;

  /* ====== New optional props ====== */
  /** Fixed viewport height. Defaults to 600 (px). Pass `"auto"` to let it grow. */
  height?: number | string;
  /** Compact = ~32px row height; Comfortable = ~40px. Default "comfortable". */
  density?: "compact" | "comfortable";
  /** Custom slot on the toolbar (left of search). */
  toolbarLeft?: ReactNode;
  /** Custom slot on the toolbar (right side, before density/columns/csv). */
  toolbarRight?: ReactNode;
  /** Render a loading state. */
  loading?: boolean;
  /** Prepend a row-index column ("1, 2, 3…"). */
  showRowNumbers?: boolean;

  /** Enable sortable columns (per-column override available via Column.sortable). */
  sortable?: boolean;
  defaultSort?: { id: string; desc?: boolean };

  /** Enable column resizing (drag the right edge of header cells). */
  resizable?: boolean;

  /** Show a column-visibility menu in the toolbar. */
  hideable?: boolean;

  /** Show a search input in the toolbar.
   *  - `true` → DataTable owns the state and filters in-memory rows.
   *  - object → controlled by the caller (use this when wiring to
   *    a server-side `?q=` param so the page refetches). */
  searchable?:
    | boolean
    | {
        value: string;
        onChange: (v: string) => void;
        placeholder?: string;
      };
  searchPlaceholder?: string;

  /** Multi-select column + bulk actions bar. */
  selection?: SelectionConfig<T>;

  /**
   * "client" — table paginates the in-memory rows.
   * Object — server-paginated; the caller drives page state.
   * `false` / undefined — no pagination, single scroll.
   */
  pagination?: "client" | ServerPagination | false;
  pageSize?: number;
  pageSizeOptions?: number[];

  /** CSV export button in the toolbar. */
  exportCsv?: { filename?: string };

  /** Force virtualization on/off. Default: auto-on if rows > 100. */
  virtualize?: boolean;
};

const DEFAULT_PAGE_SIZE = 30;
const DEFAULT_PAGE_SIZES = [10, 30, 50, 100];

const DENSITY_ROW_PX: Record<NonNullable<DataTableProps<unknown>["density"]>, number> = {
  compact: 32,
  comfortable: 40,
};

export function DataTable<T>(props: DataTableProps<T>) {
  const {
    columns: userColumns,
    rows,
    rowKey,
    onRowClick,
    expandable,
    emptyState,
    className,
    height = 600,
    density = "comfortable",
    toolbarLeft,
    toolbarRight,
    loading = false,
    showRowNumbers = false,
    sortable = false,
    defaultSort,
    resizable = false,
    hideable = false,
    searchable: searchableProp = false,
    searchPlaceholder = "Search…",
    selection,
    pagination: paginationMode = false,
    pageSize: clientPageSize = DEFAULT_PAGE_SIZE,
    pageSizeOptions = DEFAULT_PAGE_SIZES,
    exportCsv,
    virtualize: virtualizeProp,
  } = props;

  // Resolve searchable: boolean (uncontrolled) vs object (controlled
  // by the caller, e.g. wired to a server `?q=` query param).
  const isControlledSearch =
    typeof searchableProp === "object" && searchableProp !== null;
  const searchable = !!searchableProp;
  const controlledSearchValue = isControlledSearch ? searchableProp.value : "";
  const controlledSearchOnChange = isControlledSearch
    ? searchableProp.onChange
    : undefined;
  const controlledSearchPlaceholder = isControlledSearch
    ? searchableProp.placeholder
    : undefined;

  // Stable lookup of the original Column<T> by id — used for align,
  // CSV serialization, and the initial header render.
  const columnsById = useMemo(() => {
    const map = new Map<string, Column<T>>();
    for (const c of userColumns) map.set(c.key, c);
    return map;
  }, [userColumns]);

  // Local row-id helper that piggybacks on the caller's `rowKey`. We
  // pass it to TanStack via `getRowId` so selection + virtualization
  // line up regardless of array shuffling.
  const getRowId = useCallback((row: T) => rowKey(row), [rowKey]);

  // Adapt user columns into TanStack column defs. The expander +
  // selection columns are prepended via `useMemo` so they refresh
  // when the relevant props change.
  const tableColumns = useMemo<ColumnDef<T>[]>(() => {
    const cols: ColumnDef<T>[] = [];

    if (showRowNumbers) {
      cols.push({
        id: "__rowindex",
        header: () => null,
        cell: ({ row }) => (
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {row.index + 1}
          </span>
        ),
        size: 44,
        enableSorting: false,
        enableHiding: false,
        enableResizing: false,
      });
    }

    if (selection) {
      cols.push({
        id: "__select",
        header: ({ table }) => (
          <Checkbox
            aria-label="Select all rows on this page"
            checked={
              table.getIsAllPageRowsSelected()
                ? true
                : table.getIsSomePageRowsSelected()
                  ? "indeterminate"
                  : false
            }
            onCheckedChange={(v) => table.toggleAllPageRowsSelected(!!v)}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            aria-label={`Select row ${row.id}`}
            checked={row.getIsSelected()}
            onCheckedChange={(v) => row.toggleSelected(!!v)}
            onClick={(e) => e.stopPropagation()}
          />
        ),
        size: 36,
        enableSorting: false,
        enableHiding: false,
        enableResizing: false,
      });
    }

    if (expandable) {
      cols.push({
        id: "__expand",
        header: () => null,
        cell: () => null,
        size: 36,
        enableSorting: false,
        enableHiding: false,
        enableResizing: false,
      });
    }

    for (const c of userColumns) {
      const filterCfg = c.filter;
      const filterFn: FilterFn<T> | undefined = filterCfg
        ? (row, _columnId, value) => columnFilterFn(c, row.original, value)
        : undefined;
      // Real accessor → makes globalFilter (and sort) work without
      // requiring callers to duplicate value-extraction logic.
      const accessor =
        c.searchAccessor ??
        c.csvCell ??
        ((row: T) => stringifyCell(c.cell(row)));
      // Numeric / date / boolean columns can't sort lexically — caller
      // declares `sortAccessor` to provide raw values, and we wire a
      // `sortingFn` that compares them properly. CRITICAL: spread
      // conditionally — passing `sortingFn: undefined` overrides
      // TanStack's default `'auto'` (object-merge semantics) and
      // crashes the sort phase with "not a function".
      const sortAccessor = c.sortAccessor;
      cols.push({
        id: c.key,
        accessorFn: (row) => accessor(row),
        header: () => c.header,
        cell: ({ row }) => c.cell(row.original),
        enableSorting: c.sortable ?? sortable,
        enableResizing: c.resizable ?? resizable,
        enableHiding: c.hideable ?? true,
        enableColumnFilter: !!filterCfg,
        ...(filterFn ? { filterFn } : {}),
        ...(sortAccessor
          ? {
              sortingFn: ((rowA, rowB) =>
                compareSortValue(
                  sortAccessor(rowA.original),
                  sortAccessor(rowB.original),
                )) as SortingFn<T>,
            }
          : {}),
        size: parseSize(c.width) ?? undefined,
        meta: { align: c.align, userColumn: c },
      });
    }
    return cols;
  }, [userColumns, expandable, selection, showRowNumbers, sortable, resizable]);

  const hasAnyFilter = useMemo(
    () => userColumns.some((c) => !!c.filter),
    [userColumns],
  );

  // Sorting state lives inside the table.
  const [sorting, setSorting] = useState<SortingState>(
    defaultSort ? [{ id: defaultSort.id, desc: !!defaultSort.desc }] : [],
  );

  // Column visibility menu state.
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  // Global search filter (TanStack accepts a string global filter).
  const [globalFilter, setGlobalFilter] = useState("");

  // Per-column filter values (TanStack ColumnFiltersState).
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  // Column pinning state (which columns are pinned to left / right).
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>({
    left: [],
    right: [],
  });

  // Track horizontal scroll so we can fade in the inset shadow on
  // pinned columns only when there's scrolled content underneath.
  const [hScroll, setHScroll] = useState({ atLeft: true, atRight: true });

  // Density.
  const [activeDensity, setActiveDensity] =
    useState<NonNullable<DataTableProps<T>["density"]>>(density);
  useEffect(() => setActiveDensity(density), [density]);

  // Selection wiring — selection is driven by the parent's `Set<string>`,
  // so we mirror it into TanStack's RowSelection state.
  const tableRowSelection = useMemo(() => {
    if (!selection) return {};
    const out: Record<string, boolean> = {};
    for (const id of selection.rowIds) out[id] = true;
    return out;
  }, [selection]);

  // Pagination state (only used when paginationMode === "client").
  const [clientPagination, setClientPagination] = useState({
    pageIndex: 0,
    pageSize: clientPageSize,
  });
  useEffect(() => {
    setClientPagination((p) => ({ ...p, pageSize: clientPageSize }));
  }, [clientPageSize]);

  const isServerPaginated =
    typeof paginationMode === "object" && paginationMode !== null;

  const table = useReactTable<T>({
    data: rows,
    columns: tableColumns,
    getRowId,
    state: {
      sorting,
      columnVisibility,
      globalFilter,
      columnFilters,
      columnPinning,
      rowSelection: tableRowSelection,
      ...(isServerPaginated
        ? {
            pagination: {
              pageIndex: paginationMode.pageIndex,
              pageSize: paginationMode.pageSize,
            },
          }
        : paginationMode === "client"
          ? { pagination: clientPagination }
          : {}),
    },
    enableSorting: sortable,
    enableColumnResizing: resizable,
    columnResizeMode: "onChange",
    enableGlobalFilter: searchable && !isControlledSearch,
    enableHiding: hideable,
    enableRowSelection: !!selection,
    enableColumnPinning: true,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onColumnPinningChange: setColumnPinning,
    onRowSelectionChange: (updater) => {
      if (!selection) return;
      const prev: Record<string, boolean> = {};
      for (const id of selection.rowIds) prev[id] = true;
      const next = typeof updater === "function" ? updater(prev) : updater;
      const ids = new Set<string>();
      for (const [id, isSel] of Object.entries(next)) {
        if (isSel) ids.add(id);
      }
      selection.onChange(ids);
    },
    ...(isServerPaginated
      ? {
          manualPagination: true,
          pageCount: paginationMode.pageCount,
          onPaginationChange: (updater) => {
            const prev = {
              pageIndex: paginationMode.pageIndex,
              pageSize: paginationMode.pageSize,
            };
            const next = typeof updater === "function" ? updater(prev) : updater;
            if (next.pageIndex !== prev.pageIndex) {
              paginationMode.onPageChange(next.pageIndex);
            }
            if (next.pageSize !== prev.pageSize) {
              paginationMode.onPageSizeChange?.(next.pageSize);
            }
          },
        }
      : paginationMode === "client"
        ? {
            onPaginationChange: setClientPagination,
            getPaginationRowModel: getPaginationRowModel(),
          }
        : {}),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: sortable ? getSortedRowModel() : undefined,
    getFilteredRowModel:
      (searchable && !isControlledSearch) || hasAnyFilter
        ? getFilteredRowModel()
        : undefined,
  });

  const tableRows = table.getRowModel().rows;

  // Should we virtualize? Default: on if > 100 rows in view.
  const shouldVirtualize = virtualizeProp ?? tableRows.length > 100;

  const viewportRef = useRef<HTMLDivElement>(null);
  const rowHeight = DENSITY_ROW_PX[activeDensity];

  // Auto-fit columns to viewport width: scales user columns
  // proportionally so they fill the table width. Disabled the moment
  // the user drags any resize handle (or invokes autosize), matching
  // AG Grid's `sizeColumnsToFit()` semantics.
  const hasManualResizeRef = useRef(false);
  const [viewportWidth, setViewportWidth] = useState(0);

  // Update hScroll flags whenever the viewport scrolls horizontally
  // OR resizes (column resize / window resize change scrollWidth).
  // Also drives the auto-fit effect by tracking viewport width.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const update = () => {
      const max = el.scrollWidth - el.clientWidth;
      setHScroll({
        atLeft: el.scrollLeft <= 0,
        atRight: el.scrollLeft >= max - 1,
      });
    };
    const updateWidth = () => setViewportWidth(el.clientWidth);
    update();
    updateWidth();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(() => {
      update();
      updateWidth();
    });
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [columnPinning, columnVisibility]);

  // Auto-fit user columns to fill the viewport. Synth columns
  // (__select / __expand / __rowindex) keep their fixed widths.
  useEffect(() => {
    if (hasManualResizeRef.current) return;
    if (!viewportWidth) return;
    const visible = table.getVisibleLeafColumns();
    const SYNTH = new Set(["__select", "__expand", "__rowindex"]);
    let synthSum = 0;
    let userSum = 0;
    const userCols: typeof visible = [];
    for (const c of visible) {
      if (SYNTH.has(c.id)) {
        synthSum += c.getSize();
      } else {
        userSum += c.getSize();
        userCols.push(c);
      }
    }
    if (userSum <= 0 || userCols.length === 0) return;
    const target = viewportWidth - synthSum - 1; // 1px slack
    if (target <= userSum) return; // overflow case → leave scroll
    const factor = target / userSum;
    const next: Record<string, number> = {};
    let assigned = 0;
    userCols.forEach((c, i) => {
      const size =
        i === userCols.length - 1
          ? target - assigned // last col absorbs rounding remainder
          : Math.round(c.getSize() * factor);
      next[c.id] = size;
      assigned += size;
    });
    table.setColumnSizing((prev) => ({ ...prev, ...next }));
  }, [viewportWidth, columnVisibility, columnPinning, table, tableColumns.length]);
  const virtualizer = useVirtualizer({
    count: tableRows.length,
    estimateSize: () => rowHeight,
    getScrollElement: () => viewportRef.current,
    overscan: 8,
  });

  const virtualRows = shouldVirtualize ? virtualizer.getVirtualItems() : null;
  const totalSize = shouldVirtualize ? virtualizer.getTotalSize() : 0;
  const paddingTop =
    shouldVirtualize && virtualRows && virtualRows.length > 0
      ? virtualRows[0].start
      : 0;
  const paddingBottom =
    shouldVirtualize && virtualRows && virtualRows.length > 0
      ? totalSize - virtualRows[virtualRows.length - 1].end
      : 0;

  // Expanded-row tracking lives outside TanStack since `expandable`
  // is a parent-controlled render hook, not a row model feature.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // CSV export uses the *currently filtered/sorted* rows, not just
  // the visible page — operators expect "export what I see" minus
  // the page slice.
  const onExportCsv = useCallback(() => {
    if (!exportCsv) return;
    const visibleColumns = userColumns.filter((c) => {
      const id = c.key;
      const visible = columnVisibility[id];
      return visible !== false;
    });
    const headerRow = visibleColumns.map(
      (c) => c.csvHeader ?? stringifyHeader(c.header),
    );
    const dataRows = table
      .getFilteredRowModel()
      .rows.map((r) => r.original)
      .map((row) =>
        visibleColumns.map((c) =>
          c.csvCell ? c.csvCell(row) : stringifyCell(c.cell(row)),
        ),
      );
    downloadCsv(
      [headerRow, ...dataRows],
      exportCsv.filename ??
        `export-${new Date().toISOString().slice(0, 10)}.csv`,
    );
  }, [exportCsv, userColumns, columnVisibility, table]);

  const selectedCount = selection?.rowIds.size ?? 0;
  const selectedRows = useMemo(() => {
    if (!selection) return [];
    return rows.filter((r) => selection.rowIds.has(rowKey(r)));
  }, [rows, selection, rowKey]);

  // Resolve the toolbar's right-side feature buttons. The toolbar
  // always renders if any feature lives there OR if the selection
  // pill needs a place to land.
  const inSelectionMode = !!selection && selectedCount > 0;
  const showToolbar =
    searchable ||
    hideable ||
    exportCsv ||
    toolbarLeft ||
    toolbarRight ||
    inSelectionMode;

  const isEmpty = !loading && tableRows.length === 0;
  const heightStyle: React.CSSProperties =
    height === "auto"
      ? {}
      : { height: typeof height === "number" ? `${height}px` : height };

  return (
    <div className={cn("rounded-xl border bg-card overflow-hidden", className)}>
      {/* Toolbar — when rows are selected, swaps to inline selection
          state so we don't take an extra vertical row. */}
      {showToolbar && (
        <div
          className={cn(
            "border-b border-border px-3.5 h-12 flex items-center gap-2.5",
            "bg-gradient-to-b from-card to-muted/10",
            inSelectionMode &&
              "bg-gradient-to-b from-primary-light/40 to-primary-light/30 border-primary/20",
          )}
        >
          {inSelectionMode ? (
            <>
              <div className="inline-flex items-center gap-2">
                <span className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-md bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground tabular-nums">
                  {selectedCount}
                </span>
                <span className="text-xs font-medium text-foreground">
                  selected
                </span>
              </div>
              <div className="h-5 w-px bg-border/80" />
              <div className="flex-1 flex items-center gap-1.5 min-w-0 overflow-x-auto">
                {selection!.actions?.(selectedRows)}
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2.5 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => selection!.onChange(new Set())}
              >
                <X className="h-3.5 w-3.5 mr-1" /> Clear
              </Button>
            </>
          ) : (
            <>
              {searchable && (() => {
                const value = isControlledSearch
                  ? controlledSearchValue
                  : (globalFilter ?? "");
                const onChange = isControlledSearch
                  ? controlledSearchOnChange!
                  : setGlobalFilter;
                const placeholder =
                  controlledSearchPlaceholder ?? searchPlaceholder;
                return (
                <div className="relative flex-1 min-w-[220px] max-w-[360px]">
                  <Search
                    className={cn(
                      "absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none transition-colors",
                      value ? "text-primary" : "text-muted-foreground",
                    )}
                  />
                  <Input
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    className={cn(
                      "h-8 pl-8 pr-8 text-sm",
                      "border-border focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:border-primary/40",
                    )}
                  />
                  {value && (
                    <button
                      type="button"
                      onClick={() => onChange("")}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 h-5 w-5 inline-flex items-center justify-center rounded text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                      aria-label="Clear search"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              );
              })()}
              {toolbarLeft}
              <div className="flex-1" />
              {toolbarRight}
              {/* Active filters chip — when any column filter is set,
                  show a count + clear-all action. Premium UX: one click
                  resets every per-column filter. */}
              {columnFilters.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => table.resetColumnFilters()}
                    className={cn(
                      "inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xs font-medium",
                      "bg-primary/10 text-primary hover:bg-primary/15 transition-colors",
                    )}
                  >
                    <FilterIcon className="h-3.5 w-3.5" />
                    {columnFilters.length} filter
                    {columnFilters.length === 1 ? "" : "s"}
                    <X className="h-3 w-3 ml-0.5 opacity-60" />
                  </button>
                  <div className="h-5 w-px bg-border/80" />
                </>
              )}
              <div className="inline-flex items-center gap-1.5">
                <DensityToggle value={activeDensity} onChange={setActiveDensity} />
                {hideable && (
                  <ColumnVisibilityMenu table={table} userColumns={userColumns} />
                )}
                {exportCsv && (
                  <>
                    <div className="h-5 w-px bg-border/80 mx-0.5" />
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn(
                        "h-8 px-2.5 text-xs gap-1.5",
                        "hover:bg-primary/5 hover:text-primary hover:border-primary/30",
                      )}
                      onClick={onExportCsv}
                    >
                      <Download className="h-3.5 w-3.5" /> Export CSV
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Empty / loading states render in place of the viewport. */}
      {isEmpty && emptyState ? (
        <>{emptyState}</>
      ) : (
        <>
          {/* Viewport: fixed height, internal scroll, sticky header */}
          <div
            ref={viewportRef}
            className="overflow-auto relative"
            style={heightStyle}
          >
            <table
              className="w-full text-sm border-collapse"
              style={{ minWidth: table.getCenterTotalSize() || undefined }}
            >
              <thead className="sticky top-0 z-10 bg-card">
                {table.getHeaderGroups().map((hg, hgIdx) => (
                  <tr
                    key={hg.id}
                    data-header-row={hgIdx === 0 ? "main" : undefined}
                    className="bg-muted/30 border-b border-border text-left"
                  >
                    {hg.headers.map((h, hIdx) => {
                      const meta = h.column.columnDef.meta as
                        | { align?: string; userColumn?: Column<T> }
                        | undefined;
                      const align = meta?.align ?? "left";
                      const userCol = meta?.userColumn;
                      const canSort = h.column.getCanSort();
                      const sortDir = h.column.getIsSorted();
                      const isFiltered = h.column.getIsFiltered();
                      const isLast = hIdx === hg.headers.length - 1;
                      const showMenu = !!userCol && !userCol.noMenu;
                      const pinned = pinnedCellStyle(h.column, hScroll);
                      return (
                        <th
                          key={h.id}
                          scope="col"
                          aria-sort={
                            sortDir === "asc"
                              ? "ascending"
                              : sortDir === "desc"
                                ? "descending"
                                : "none"
                          }
                          style={{
                            width: h.getSize() || undefined,
                            ...pinned.style,
                          }}
                          className={cn(
                            "relative px-3 h-9 text-xs font-medium text-foreground bg-muted/30",
                            "border-b border-border",
                            !isLast && "border-r border-border/50",
                            "group/th",
                            pinned.cls,
                          )}
                        >
                          {h.isPlaceholder ? null : (
                            <div
                              className={cn(
                                "flex items-center gap-1.5 min-w-0",
                                align === "right" && "justify-end",
                                align === "center" && "justify-center",
                              )}
                            >
                              <button
                                type="button"
                                onClick={
                                  canSort
                                    ? h.column.getToggleSortingHandler()
                                    : undefined
                                }
                                className={cn(
                                  "inline-flex items-center gap-1.5 select-none min-w-0 truncate",
                                  canSort && "cursor-pointer hover:text-primary",
                                  !canSort && "cursor-default",
                                )}
                              >
                                <span className="truncate">
                                  {flexRender(
                                    h.column.columnDef.header,
                                    h.getContext(),
                                  )}
                                </span>
                                {canSort && (
                                  <span className="text-muted-foreground shrink-0">
                                    {sortDir === "asc" ? (
                                      <ArrowUp className="h-3 w-3" />
                                    ) : sortDir === "desc" ? (
                                      <ArrowDown className="h-3 w-3" />
                                    ) : (
                                      <ArrowUpDown className="h-3 w-3 opacity-30" />
                                    )}
                                  </span>
                                )}
                              </button>
                              {isFiltered && (
                                <FilterIcon
                                  className="h-3 w-3 text-primary shrink-0"
                                  aria-label="Column filter active"
                                />
                              )}
                              {showMenu && (
                                <HeaderMenu
                                  column={h.column}
                                  table={table}
                                  viewportRef={viewportRef}
                                  onUserSize={() => {
                                    hasManualResizeRef.current = true;
                                  }}
                                />
                              )}
                            </div>
                          )}
                          {/* Resize handle — drag to size, dblclick to autosize.
                              Both interactions opt the table out of auto-fit
                              so the user's chosen widths stick. */}
                          {h.column.getCanResize() && (
                            <span
                              onMouseDown={(e) => {
                                hasManualResizeRef.current = true;
                                h.getResizeHandler()(e);
                              }}
                              onTouchStart={(e) => {
                                hasManualResizeRef.current = true;
                                h.getResizeHandler()(e);
                              }}
                              onDoubleClick={() => {
                                hasManualResizeRef.current = true;
                                autoSizeColumn(
                                  h.column.id,
                                  table,
                                  viewportRef.current,
                                );
                              }}
                              className={cn(
                                "absolute top-0 right-0 h-full w-1 cursor-col-resize select-none touch-none",
                                "hover:bg-primary/40",
                                h.column.getIsResizing() && "bg-primary",
                              )}
                            />
                          )}
                        </th>
                      );
                    })}
                  </tr>
                ))}
                {hasAnyFilter && (
                  <tr className="border-b border-border bg-card">
                    {table.getHeaderGroups()[0]?.headers.map((h, hIdx) => {
                      const isLast =
                        hIdx === table.getHeaderGroups()[0].headers.length - 1;
                      const meta = h.column.columnDef.meta as
                        | { userColumn?: Column<T> }
                        | undefined;
                      const userCol = meta?.userColumn;
                      const pinned = pinnedCellStyle(h.column, hScroll);
                      return (
                        <th
                          key={`flt-${h.id}`}
                          style={{
                            width: h.getSize() || undefined,
                            ...pinned.style,
                          }}
                          className={cn(
                            "px-2 py-1.5 align-middle bg-card",
                            !isLast && "border-r border-border/50",
                            pinned.cls,
                          )}
                        >
                          {userCol?.filter ? (
                            <FilterCell
                              column={h.column}
                              userColumn={userCol}
                              rows={rows}
                            />
                          ) : null}
                        </th>
                      );
                    })}
                  </tr>
                )}
              </thead>
              <tbody>
                {loading && tableRows.length === 0 ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <SkeletonRow
                      key={`skeleton-${i}`}
                      columns={table.getVisibleLeafColumns()}
                      height={rowHeight}
                    />
                  ))
                ) : (
                  <>
                {paddingTop > 0 && (
                  <tr aria-hidden>
                    <td style={{ height: paddingTop }} colSpan={table.getAllColumns().length} />
                  </tr>
                )}
                {(virtualRows
                  ? virtualRows.map((vr) => tableRows[vr.index])
                  : tableRows
                ).map((row) => {
                  const expandedDetail = expandable?.(row.original) ?? null;
                  const isOpen = expanded[row.id] ?? false;
                  const visibleCells = row.getVisibleCells();
                  return (
                    <Fragment key={row.id}>
                      <tr
                        onClick={
                          onRowClick ? () => onRowClick(row.original) : undefined
                        }
                        aria-rowindex={row.index + 1}
                        data-selected={row.getIsSelected() ? "true" : "false"}
                        className={cn(
                          "border-b border-border/60 last:border-0 group transition-colors",
                          onRowClick && "cursor-pointer hover:bg-muted/50",
                          !onRowClick && "hover:bg-muted/30",
                          row.getIsSelected() && "bg-primary-light/30 hover:bg-primary-light/40",
                        )}
                        style={{ height: rowHeight }}
                      >
                        {visibleCells.map((cell, cIdx) => {
                          const isExpander = cell.column.id === "__expand";
                          const isSelectCol = cell.column.id === "__select";
                          const isRowIndex = cell.column.id === "__rowindex";
                          const meta = cell.column.columnDef.meta as
                            | { align?: string }
                            | undefined;
                          const align = meta?.align ?? "left";
                          const isLast = cIdx === visibleCells.length - 1;
                          const dividerClass = !isLast ? "border-r border-border/40" : "";
                          const pinned = pinnedCellStyle(cell.column, hScroll);
                          const pinnedBg = pinned.pinned
                            ? "bg-card group-data-[selected=true]:bg-primary-light/30"
                            : "";
                          if (isExpander) {
                            return (
                              <td
                                key={cell.id}
                                style={pinned.style}
                                className={cn(
                                  "px-2 align-middle",
                                  dividerClass,
                                  pinnedBg,
                                  pinned.cls,
                                )}
                              >
                                {expandedDetail !== null ? (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setExpanded((p) => ({ ...p, [row.id]: !p[row.id] }));
                                    }}
                                    className="h-6 w-6 inline-flex items-center justify-center rounded hover:bg-muted text-muted-foreground"
                                    aria-label={isOpen ? "Collapse row" : "Expand row"}
                                    aria-expanded={isOpen}
                                  >
                                    {isOpen ? (
                                      <ChevronDown className="h-4 w-4" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4" />
                                    )}
                                  </button>
                                ) : null}
                              </td>
                            );
                          }
                          if (isSelectCol) {
                            return (
                              <td
                                key={cell.id}
                                style={pinned.style}
                                className={cn(
                                  "px-3 align-middle",
                                  dividerClass,
                                  pinnedBg,
                                  pinned.cls,
                                )}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext(),
                                )}
                              </td>
                            );
                          }
                          if (isRowIndex) {
                            return (
                              <td
                                key={cell.id}
                                style={pinned.style}
                                className={cn(
                                  "px-3 align-middle text-center",
                                  dividerClass,
                                  pinnedBg,
                                  pinned.cls,
                                )}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext(),
                                )}
                              </td>
                            );
                          }
                          return (
                            <td
                              key={cell.id}
                              data-col-id={cell.column.id}
                              style={{
                                width: cell.column.getSize() || undefined,
                                ...pinned.style,
                              }}
                              className={cn(
                                "px-3 align-middle",
                                dividerClass,
                                align === "right" && "text-right",
                                align === "center" && "text-center",
                                pinnedBg,
                                pinned.cls,
                              )}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </td>
                          );
                        })}
                      </tr>
                      {expandable && isOpen && expandedDetail && (
                        <tr className="border-b border-border last:border-0 bg-muted/20">
                          <td colSpan={row.getVisibleCells().length} className="px-4 py-4">
                            {expandedDetail}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                {paddingBottom > 0 && (
                  <tr aria-hidden>
                    <td style={{ height: paddingBottom }} colSpan={table.getAllColumns().length} />
                  </tr>
                )}
                  </>
                )}
              </tbody>
            </table>
            {loading && tableRows.length > 0 && (
              <div className="absolute top-0 left-0 right-0 h-0.5 z-20 bg-primary/60 animate-pulse pointer-events-none" />
            )}
          </div>

          {/* Footer pagination */}
          {paginationMode && <PaginationFooter
            table={table}
            paginationMode={paginationMode}
            pageSizeOptions={pageSizeOptions}
          />}
        </>
      )}
    </div>
  );

  // ↓ TS doesn't like Column<T> prop name shadowing — use the
  // private name internally.
  /* eslint-disable @typescript-eslint/no-unused-vars */ void columnsById;
}

/* ====== Sub-components ====== */

function HeaderMenu<T>({
  column,
  table,
  viewportRef,
  onUserSize,
}: {
  column: TableColumn<T, unknown>;
  table: TableInstance<T>;
  viewportRef: React.RefObject<HTMLDivElement | null>;
  onUserSize?: () => void;
}) {
  const canSort = column.getCanSort();
  const canHide = column.getCanHide();
  const canResize = column.getCanResize();
  const canPin = column.getCanPin();
  const pinned = column.getIsPinned();
  const sortDir = column.getIsSorted();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Column menu"
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "ml-auto h-5 w-5 inline-flex items-center justify-center rounded shrink-0",
            "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
            "opacity-0 group-hover/th:opacity-100 focus-visible:opacity-100",
            "data-[state=open]:opacity-100",
          )}
        >
          <MoreVertical className="h-3 w-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {canSort && (
          <>
            <DropdownMenuItem
              disabled={sortDir === "asc"}
              onClick={() => column.toggleSorting(false)}
            >
              <ArrowUp className="h-3.5 w-3.5 mr-2" /> Sort ascending
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={sortDir === "desc"}
              onClick={() => column.toggleSorting(true)}
            >
              <ArrowDown className="h-3.5 w-3.5 mr-2" /> Sort descending
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={!sortDir}
              onClick={() => column.clearSorting()}
            >
              <ArrowUpDown className="h-3.5 w-3.5 mr-2" /> Clear sort
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        {canPin && (
          <>
            <DropdownMenuItem
              disabled={pinned === "left"}
              onClick={() => column.pin("left")}
            >
              <ArrowLeftToLine className="h-3.5 w-3.5 mr-2" /> Pin left
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={pinned === "right"}
              onClick={() => column.pin("right")}
            >
              <ArrowRightToLine className="h-3.5 w-3.5 mr-2" /> Pin right
            </DropdownMenuItem>
            {pinned && (
              <DropdownMenuItem onClick={() => column.pin(false)}>
                <PinOff className="h-3.5 w-3.5 mr-2" /> Unpin column
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
          </>
        )}
        {canResize && (
          <DropdownMenuItem
            onClick={() => {
              onUserSize?.();
              autoSizeColumn(column.id, table, viewportRef.current);
            }}
          >
            <Maximize2 className="h-3.5 w-3.5 mr-2" /> Autosize column
          </DropdownMenuItem>
        )}
        {canHide && (
          <DropdownMenuItem onClick={() => column.toggleVisibility(false)}>
            <EyeOff className="h-3.5 w-3.5 mr-2" /> Hide column
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function FilterCell<T>({
  column,
  userColumn,
  rows,
}: {
  column: TableColumn<T, unknown>;
  userColumn: Column<T>;
  rows: T[];
}) {
  const f = userColumn.filter!;
  const value = column.getFilterValue();
  const set = (v: unknown) => column.setFilterValue(v);

  const baseInputClass =
    "h-7 w-full px-2 text-xs border border-border rounded-md bg-background placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/30";

  switch (f.type) {
    case "text":
      return (
        <div className="relative">
          <input
            type="text"
            placeholder={f.placeholder ?? "Filter…"}
            value={(value as string) ?? ""}
            onChange={(e) => set(e.target.value || undefined)}
            className={cn(baseInputClass, "pr-6")}
          />
          {value ? (
            <button
              type="button"
              onClick={() => set(undefined)}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-5 w-5 inline-flex items-center justify-center rounded text-muted-foreground hover:bg-muted/60"
              aria-label="Clear filter"
            >
              <X className="h-3 w-3" />
            </button>
          ) : null}
        </div>
      );

    case "number": {
      const v = (value as { min?: string; max?: string } | undefined) ?? {};
      return (
        <div className="flex items-center gap-1">
          <input
            type="number"
            placeholder="≥"
            value={v.min ?? ""}
            onChange={(e) =>
              set({
                ...v,
                min: e.target.value || undefined,
              })
            }
            className={cn(baseInputClass, "w-1/2 tabular-nums")}
          />
          <input
            type="number"
            placeholder="≤"
            value={v.max ?? ""}
            onChange={(e) =>
              set({
                ...v,
                max: e.target.value || undefined,
              })
            }
            className={cn(baseInputClass, "w-1/2 tabular-nums")}
          />
        </div>
      );
    }

    case "date":
      return (
        <input
          type="date"
          value={(value as string) ?? ""}
          onChange={(e) => set(e.target.value || undefined)}
          className={baseInputClass}
        />
      );

    case "select": {
      const explicit = f.options;
      const options = useMemoOptions(explicit, userColumn, rows);
      const current = (value as string) ?? "";
      return (
        <select
          value={current}
          onChange={(e) => set(e.target.value || undefined)}
          className={cn(baseInputClass, "appearance-none pr-7 cursor-pointer")}
        >
          <option value="">All</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label ?? o.value}
            </option>
          ))}
        </select>
      );
    }

    case "boolean": {
      const v = value === undefined ? "any" : (value as string);
      return (
        <select
          value={v}
          onChange={(e) =>
            set(e.target.value === "any" ? undefined : e.target.value === "true")
          }
          className={cn(baseInputClass, "appearance-none pr-7 cursor-pointer")}
        >
          <option value="any">Any</option>
          <option value="true">{f.trueLabel ?? "Yes"}</option>
          <option value="false">{f.falseLabel ?? "No"}</option>
        </select>
      );
    }

    case "custom":
      return <>{f.render(value, set, rows)}</>;
  }
}

function useMemoOptions<T>(
  explicit: Array<{ value: string; label?: string }> | undefined,
  userColumn: Column<T>,
  rows: T[],
): Array<{ value: string; label?: string }> {
  return useMemo(() => {
    if (explicit && explicit.length > 0) return explicit;
    const seen = new Set<string>();
    const out: Array<{ value: string; label?: string }> = [];
    for (const r of rows) {
      const v = getFilterValue(userColumn, r);
      if (v == null || v === "") continue;
      const s = String(v);
      if (seen.has(s)) continue;
      seen.add(s);
      out.push({ value: s });
      if (out.length > 50) break; // sanity cap
    }
    out.sort((a, b) => a.value.localeCompare(b.value));
    return out;
  }, [explicit, userColumn, rows]);
}

/** Measure widest visible cell content in a column and resize to fit. */
function autoSizeColumn<T>(
  columnId: string,
  table: TableInstance<T>,
  viewport: HTMLDivElement | null,
) {
  if (!viewport) return;
  const cells = viewport.querySelectorAll<HTMLElement>(
    `[data-col-id="${CSS.escape(columnId)}"]`,
  );
  let max = 0;
  cells.forEach((td) => {
    // scrollWidth of the inner content = the natural width before truncation.
    const inner = td.firstElementChild as HTMLElement | null;
    const w = inner ? inner.scrollWidth : td.scrollWidth;
    if (w > max) max = w;
  });
  if (max <= 0) return;
  const next = Math.min(Math.max(max + 28, 60), 480);
  table.setColumnSizing((prev) => ({ ...prev, [columnId]: next }));
}

function SkeletonRow({
  columns,
  height,
}: {
  columns: Array<{ id: string; getSize: () => number }>;
  height: number;
}) {
  return (
    <tr style={{ height }} className="border-b border-border/60">
      {columns.map((c, idx) => {
        const isLast = idx === columns.length - 1;
        const widthPct = idx === 0 ? "70%" : idx % 2 === 0 ? "50%" : "60%";
        return (
          <td
            key={c.id}
            style={{ width: c.getSize() || undefined }}
            className={cn("px-3", !isLast && "border-r border-border/40")}
          >
            <div
              className="h-3 rounded bg-muted/70 animate-pulse"
              style={{ width: widthPct }}
            />
          </td>
        );
      })}
    </tr>
  );
}

function DensityToggle({
  value,
  onChange,
}: {
  value: "compact" | "comfortable";
  onChange: (v: "compact" | "comfortable") => void;
}) {
  return (
    <div className="inline-flex items-center rounded-md border border-border overflow-hidden h-8 bg-card">
      <button
        type="button"
        onClick={() => onChange("compact")}
        aria-label="Compact density"
        title="Compact density"
        className={cn(
          "h-full px-2 inline-flex items-center justify-center transition-colors",
          value === "compact"
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:bg-muted/40 hover:text-foreground",
        )}
      >
        <Rows4 className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={() => onChange("comfortable")}
        aria-label="Comfortable density"
        title="Comfortable density"
        className={cn(
          "h-full px-2 inline-flex items-center justify-center transition-colors border-l border-border",
          value === "comfortable"
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:bg-muted/40 hover:text-foreground",
        )}
      >
        <Rows3 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function ColumnVisibilityMenu<T>({
  table,
  userColumns,
}: {
  table: ReturnType<typeof useReactTable<T>>;
  userColumns: Column<T>[];
}) {
  const userIds = new Set(userColumns.map((c) => c.key));
  const hideableColumns = table
    .getAllLeafColumns()
    .filter((c) => userIds.has(c.id) && c.getCanHide());

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "h-8 px-2.5 text-xs gap-1.5",
            "hover:bg-primary/5 hover:text-primary hover:border-primary/30",
          )}
        >
          <Columns3 className="h-3.5 w-3.5" /> Columns
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
          Toggle columns
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {hideableColumns.map((c) => {
          const userCol = userColumns.find((uc) => uc.key === c.id);
          const label = stringifyHeader(userCol?.header) || c.id;
          return (
            <DropdownMenuCheckboxItem
              key={c.id}
              checked={c.getIsVisible()}
              onCheckedChange={(v) => c.toggleVisibility(!!v)}
              onSelect={(e) => e.preventDefault()}
              className="capitalize"
            >
              {label}
            </DropdownMenuCheckboxItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PaginationFooter<T>({
  table,
  paginationMode,
  pageSizeOptions,
}: {
  table: ReturnType<typeof useReactTable<T>>;
  paginationMode: "client" | ServerPagination;
  pageSizeOptions: number[];
}) {
  const isServer = typeof paginationMode === "object";
  const pageIndex = isServer
    ? paginationMode.pageIndex
    : table.getState().pagination.pageIndex;
  const pageSize = isServer
    ? paginationMode.pageSize
    : table.getState().pagination.pageSize;
  const pageCount = isServer
    ? Math.max(paginationMode.pageCount, 1)
    : Math.max(table.getPageCount(), 1);
  const totalRows = isServer
    ? paginationMode.totalRows
    : table.getFilteredRowModel().rows.length;
  const startRow = totalRows ? pageIndex * pageSize + 1 : 0;
  const endRow = totalRows
    ? Math.min((pageIndex + 1) * pageSize, totalRows)
    : 0;

  const setPageSize = (v: number) => {
    if (isServer) {
      paginationMode.onPageSizeChange?.(v);
    } else {
      table.setPageSize(v);
    }
  };

  // Always include the current pageSize in the dropdown so it
  // shows a checkmark even when the page picks a non-standard
  // default (e.g. service tabs use 20).
  const allPageSizeOptions = useMemo(() => {
    const set = new Set([...pageSizeOptions, pageSize]);
    return [...set].sort((a, b) => a - b);
  }, [pageSizeOptions, pageSize]);

  return (
    <div className="border-t border-border px-3 h-9 flex items-center justify-between gap-3 text-[11px] text-muted-foreground bg-muted/10">
      <p>
        {totalRows && totalRows > 0 ? (
          <>
            <span className="text-foreground font-medium tabular-nums">{startRow}</span>
            –<span className="text-foreground font-medium tabular-nums">{endRow}</span>
            {" of "}
            <span className="text-foreground font-medium tabular-nums">{totalRows}</span>
            {" "}rows
          </>
        ) : (
          "No rows"
        )}
      </p>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5">
          <span>Rows per page</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-6 px-2 text-[11px] gap-1 tabular-nums"
              >
                {pageSize} <ChevronUp className="h-2.5 w-2.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[6rem]">
              {allPageSizeOptions.map((n) => (
                <DropdownMenuItem
                  key={n}
                  onClick={() => setPageSize(n)}
                  className={cn(
                    "text-sm justify-between tabular-nums cursor-pointer",
                    n === pageSize && "text-primary font-medium",
                  )}
                >
                  <span>{n}</span>
                  {n === pageSize && <Check className="h-3.5 w-3.5" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="h-3.5 w-px bg-border/80" />
        <div className="flex items-center gap-0.5">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            disabled={pageIndex <= 0}
            onClick={() =>
              isServer
                ? paginationMode.onPageChange(pageIndex - 1)
                : table.previousPage()
            }
            aria-label="Previous page"
          >
            <ChevronLeft className="h-3 w-3" />
          </Button>
          <span className="px-1.5 tabular-nums">
            <span className="text-foreground font-medium">{pageIndex + 1}</span>
            <span className="text-muted-foreground/70"> / {pageCount}</span>
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            disabled={pageIndex >= pageCount - 1}
            onClick={() =>
              isServer
                ? paginationMode.onPageChange(pageIndex + 1)
                : table.nextPage()
            }
            aria-label="Next page"
          >
            <ChevronRight className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ====== Helpers ====== */

/** Build sticky-positioning style + classes for a pinned column cell.
 *  When unpinned, returns empty style/class. The shadow only shows when
 *  there's scrolled content "underneath" the pinned edge.
 *  Caller is responsible for adding an opaque background colour so the
 *  cell occludes content scrolling beneath it. */
function pinnedCellStyle<T>(
  column: TableColumn<T, unknown>,
  hScroll: { atLeft: boolean; atRight: boolean },
): { style: React.CSSProperties; cls: string; pinned: false | "left" | "right" } {
  const pinned = column.getIsPinned();
  if (!pinned) return { style: {}, cls: "", pinned: false };
  const isLast = column.getIsLastColumn(pinned);
  const isFirst = column.getIsFirstColumn(pinned);
  if (pinned === "left") {
    return {
      pinned: "left",
      style: { position: "sticky", left: column.getStart("left"), zIndex: 1 },
      cls: cn(
        isLast && !hScroll.atLeft && "shadow-[inset_-6px_0_4px_-4px_rgba(0,0,0,0.08)]",
      ),
    };
  }
  return {
    pinned: "right",
    style: { position: "sticky", right: column.getAfter("right"), zIndex: 1 },
    cls: cn(
      isFirst && !hScroll.atRight && "shadow-[inset_6px_0_4px_-4px_rgba(0,0,0,0.08)]",
    ),
  };
}

/** Resolve the underlying value used for filter / autosize when a column
 *  declares an accessor; otherwise stringify the rendered cell node. */
function getFilterValue<T>(col: Column<T>, row: T): unknown {
  if (col.filterAccessor) return col.filterAccessor(row);
  if (col.csvCell) return col.csvCell(row);
  return stringifyCell(col.cell(row));
}

/** Per-column filterFn implementing each `ColumnFilterType`. */
function columnFilterFn<T>(col: Column<T>, row: T, value: unknown): boolean {
  if (value == null || value === "") return true;
  const f = col.filter;
  if (!f) return true;
  const cellRaw = getFilterValue(col, row);

  switch (f.type) {
    case "text": {
      const q = String(value).toLowerCase().trim();
      if (!q) return true;
      return String(cellRaw ?? "")
        .toLowerCase()
        .includes(q);
    }
    case "number": {
      const v = value as { min?: string; max?: string };
      const num = typeof cellRaw === "number" ? cellRaw : Number(cellRaw);
      if (Number.isNaN(num)) return false;
      if (v.min !== undefined && v.min !== "" && num < Number(v.min)) return false;
      if (v.max !== undefined && v.max !== "" && num > Number(v.max)) return false;
      return true;
    }
    case "date": {
      const target = String(value);
      if (!target) return true;
      const cellDate = cellRaw instanceof Date
        ? cellRaw
        : cellRaw
          ? new Date(String(cellRaw))
          : null;
      if (!cellDate || Number.isNaN(cellDate.getTime())) return false;
      // Match if same calendar day.
      const t = new Date(target);
      return (
        cellDate.getFullYear() === t.getFullYear() &&
        cellDate.getMonth() === t.getMonth() &&
        cellDate.getDate() === t.getDate()
      );
    }
    case "select": {
      const set = value as string[] | string;
      if (Array.isArray(set)) {
        if (set.length === 0) return true;
        return set.includes(String(cellRaw ?? ""));
      }
      return String(cellRaw ?? "") === String(set);
    }
    case "boolean": {
      if (value === "any" || value === "") return true;
      const want = value === true || value === "true";
      const have = cellRaw === true || String(cellRaw).toLowerCase() === "true";
      return want === have;
    }
    case "custom": {
      // Custom filter — caller renders the input AND owns the meaning of value.
      // For coarse default, treat any truthy value as "matches all".
      return true;
    }
    default:
      return true;
  }
}

function parseSize(width: string | undefined): number | undefined {
  if (!width) return undefined;
  const m = width.match(/^(\d+(?:\.\d+)?)(px)?$/i);
  if (m) return Number(m[1]);
  return undefined;
}

function stringifyHeader(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node);
  return "";
}

/** Recursively extract plain text from a React node. Walks element
 *  children + fragment + array children. Used as the default sort /
 *  search key when a column doesn't declare an explicit accessor.
 *  For accurate CSV output of complex cells, callers should still
 *  declare `csvCell`. */
function stringifyCell(node: ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node))
    return node.map(stringifyCell).filter(Boolean).join(" ");
  if (typeof node === "object" && "props" in node) {
    const el = node as ReactElement<{ children?: ReactNode }>;
    return stringifyCell(el.props.children ?? "");
  }
  return "";
}

/** Sort comparator that handles numbers / Dates / booleans / strings
 *  with a sensible fallback for mixed types. Null / undefined sort
 *  to the end (matches AG Grid + TanStack defaults). */
function compareSortValue(a: unknown, b: unknown): number {
  const aNil = a == null;
  const bNil = b == null;
  if (aNil && bNil) return 0;
  if (aNil) return 1;
  if (bNil) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (a instanceof Date && b instanceof Date)
    return a.getTime() - b.getTime();
  if (typeof a === "boolean" && typeof b === "boolean")
    return Number(a) - Number(b);
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}

function downloadCsv(rows: string[][], filename: string) {
  const csv = rows
    .map((row) =>
      row
        .map((cell) => {
          const s = cell ?? "";
          if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
          return s;
        })
        .join(","),
    )
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Pencil `bjvYK` (`v2/FieldPair`). Used inside expanded rows + the detail
 * panel for label/value rows. Re-exported here for backward compat.
 */
export function FieldPair({
  label,
  value,
  className,
}: {
  label: string;
  value: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex items-start gap-4 py-2", className)}>
      <p className="text-xs text-muted-foreground w-32 shrink-0 pt-0.5">{label}</p>
      <p className="text-sm text-foreground flex-1 min-w-0">{value}</p>
    </div>
  );
}
